<template>
  <div class="h-full">
    <BaseTable
      v-if="measurementUnits"
      :headers="tableHeaders"
      :list="filteredMeasurementUnits"
      :total-records="measurementUnitsTotalCount"
      :page="measurementUnitsPageNumber"
      :items-per-page="measurementUnitsItemsPerPage"
      table-id="measurement-units"
      is-saving-layout-hidden
      clickable
      hide-grouping
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @table-item-clicked="onEdit">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5">
          <!-- Col 1 -->
          <div class="space-y-4 justify-self-start">
            <div>
              <BaseTriStateToggle
                :label="$t('representative.isMapped')"
                name="isInactiveFilter"
                v-model="filterIsMapped" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>

    <DCMeasurementUnitModalCreateEdit
      v-if="createEditModalActive && currentMeasurementUnit"
      :measurement-unit="currentMeasurementUnit"
      :mode="createEditModalMode"
      :view-only="mode === 'view'"
      :representative-id="representativeId"
      :existing-measurement-units="measurementUnits"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import BaseTable from '@/components/common/BaseTable.vue';
import DCMeasurementUnitModalCreateEdit from '@/components/traders/DCMeasurementUnitModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseTriStateToggle from '../common/BaseTriStateToggle.vue';

import { RecordMode, type TableFilterData } from '@/types/Base';
import { deepCopy } from '@/utils/data';
import { getDCMeasurementUnitsWithConditions } from '@/services/api/dataCollection';
import type { DCMeasurementUnit, RepresentativeFiltersData } from '@/types/DataCollection';

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  resetState: boolean;
  representativeId: string;
}>();

const measurementUnits = defineModel<DCMeasurementUnit[]>('measurementUnits');
const measurementUnitModified = defineModel<boolean>('isMeasurementUnitModified', {
  default: false,
});
const filterIsMapped = ref<boolean | undefined>(undefined);

const originalMeasurementUnits = ref<DCMeasurementUnit[]>([]);
const measurementUnitsTotalCount = ref<number>(0);
const measurementUnitsItemsPerPage = ref(50);
const measurementUnitsPageNumber = ref(1);

const filteredMeasurementUnits = computed<DCMeasurementUnit[]>(() => {
  if (!measurementUnits.value) return [];
  let list = measurementUnits.value.filter((x) => x.recordMode !== RecordMode.Deleted);

  return list;
});

watch(
  () => measurementUnits.value,
  (newData) => {
    measurementUnitModified.value =
      JSON.stringify(newData) !== JSON.stringify(originalMeasurementUnits.value);
  },
  { deep: true }
);

async function loadMeasurementUnits(
  params?: TableFilterData & {
    isMapped?: boolean;
  }
) {
  const queryParams = {
    representativeId: props.representativeId,
    page: params?.page || measurementUnitsPageNumber.value,
    itemsPerPage: params?.itemsPerPage || measurementUnitsItemsPerPage.value,
    query: params?.query || '',
    sortColumn: params?.column !== undefined ? tableHeaders[params.column]?.value : undefined,
    sortAscending: params?.order === 'asc' || params?.order === undefined,
    isMapped: params?.isMapped,
  };
  const res = await getDCMeasurementUnitsWithConditions(queryParams);
  if (res) {
    originalMeasurementUnits.value = res.data;
    measurementUnits.value = deepCopy<DCMeasurementUnit[]>(originalMeasurementUnits.value);
    measurementUnitsTotalCount.value = res.count;

    if (params?.page !== undefined) {
      measurementUnitsPageNumber.value = params.page;
    }
    if (params?.itemsPerPage !== undefined) {
      measurementUnitsItemsPerPage.value = params.itemsPerPage;
    }
  }
}

watch(
  () => props.resetState,
  async (newVal, oldVal) => {
    if (props.representativeId && (oldVal === undefined || (newVal && oldVal !== newVal))) {
      await loadMeasurementUnits();
    }
  },
  { immediate: true }
);

const { t: $t } = useI18n();

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');
const currentMeasurementUnit = ref<DCMeasurementUnit | undefined>(undefined);

const tableHeaders = [
  { value: 'measurementUnit.name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'measurementUnit.code', text: $t('baseObject.code'), type: 'text', active: true },
  {
    value: 'measurementUnitRemotedCode',
    text: $t('representative.remotedCode'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initMeasurementUnit(): DCMeasurementUnit {
  return {
    id: uuidv4(),
    representativeId: props.representativeId,
    measurementUnitRemotedCode: '',
    measurementUnitId: '',
    measurementUnit: {
      id: '',
      name: '',
    },
    isActive: true,
    isFromManager: true,
    recordMode: RecordMode.Added,
  };
}

function onAddNew() {
  currentMeasurementUnit.value = initMeasurementUnit();
  createEditModalMode.value = 'add';
  createEditModalActive.value = true;
}

function onEdit(item: DCMeasurementUnit) {
  currentMeasurementUnit.value = deepCopy(item);
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DCMeasurementUnit>();

function onDeleteRow(item: DCMeasurementUnit) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  if (!deleteRowItem.value || !measurementUnits.value) return;
  modalDeleteRow.value = false;
  const index = measurementUnits.value?.findIndex((x) => x.id === deleteRowItem.value?.id);
  if (index !== -1) {
    const existingItem = measurementUnits.value[index];
    if (existingItem.recordMode === RecordMode.Added) {
      measurementUnits.value.splice(index, 1);
    } else if (existingItem.recordMode === RecordMode.Modified) {
      existingItem.recordMode = RecordMode.Deleted;
    } else {
      existingItem.recordMode = RecordMode.Deleted;
    }
  }
}

function onDeleteMultiselect(items: DCMeasurementUnit[]) {
  modalDeleteRow.value = false;

  items.forEach((item) => {
    if (!measurementUnits.value) return;
    if (item.isFromManager) {
      const existingIndex = measurementUnits.value.findIndex((x) => x.id === item.id);
      //exist
      if (existingIndex !== -1) {
        const existingItem = measurementUnits.value[existingIndex];
        if (existingItem.recordMode === RecordMode.Added) {
          measurementUnits.value.splice(existingIndex, 1);
        } else if (existingItem.recordMode === RecordMode.Modified) {
          existingItem.recordMode = RecordMode.Deleted;
        } else {
          existingItem.recordMode = RecordMode.Deleted;
        }
      } else {
        return;
      }
    }
  });
}

async function onSave(unit: DCMeasurementUnit) {
  if (!measurementUnits.value) return;
  createEditModalActive.value = false;
  const existingIndex = measurementUnits.value?.findIndex((x) => x.id === unit.id);
  if (existingIndex !== -1) {
    unit.recordMode = RecordMode.Modified;
    measurementUnits.value[existingIndex] = unit;
  } else {
    unit.recordMode = RecordMode.Added;
    measurementUnits.value.push(unit);
  }
}

const filterState = reactive<{
  query: string;
  column?: number;
  order?: 'asc' | 'desc';
  isMapped?: boolean;
}>({
  query: '',
  column: undefined,
  order: undefined,
  isMapped: undefined,
});

async function onRefreshData(
  options: TableFilterData & {
    isMapped?: boolean;
  }
) {
  filterState.query = options.query ?? '';
  if (options.column !== undefined && options.order) {
    filterState.column = options.column;
    filterState.order = options.order;
  }
  filterState.isMapped = options.isMapped;

  await loadMeasurementUnits(options);
}

async function onApplyFilters(data: RepresentativeFiltersData) {
  data.isMapped = filterIsMapped.value;
  await onRefreshData(data);
}

async function onResetFilters() {
  filterIsMapped.value = undefined;
  await onRefreshData({});
}
</script>
